import { useState } from "react";
import { api } from "./api";
import { toast } from "sonner";
import { Plus, Pencil, Trash2 } from "lucide-react";
import { useResource, SectionHeader, Loading, Modal, ConfirmDialog, Field, PrimaryBtn, GhostBtn, ImagePicker, inputCls } from "./components";

const EMPTY = { name: "", college: "", team: "", position: "", grad_year: "", bio: "", photo: "" };
const CURRENT_YEAR = new Date().getFullYear();
const YEARS = Array.from({ length: 12 }, (_, i) => CURRENT_YEAR - i);

export default function CollegeSection() {
  const { items, loading, reload } = useResource("college");
  const [editing, setEditing] = useState(null);
  const [deleting, setDeleting] = useState(null);
  const [saving, setSaving] = useState(false);

  const sorted = [...items].sort((a, b) => (b.grad_year ?? 0) - (a.grad_year ?? 0));

  const save = async () => {
    if (!editing.name.trim() || !editing.college.trim()) {
      toast.error("Please enter the player's name and college.");
      return;
    }
    setSaving(true);
    const payload = {
      name: editing.name.trim(),
      college: editing.college.trim(),
      team: (editing.team || "").trim(),
      position: (editing.position || "").trim(),
      grad_year: Number(editing.grad_year) || null,
      bio: (editing.bio || "").trim(),
      photo: editing.photo || "",
    };
    try {
      if (editing.id) await api.put(`/admin/college/${editing.id}`, payload);
      else await api.post("/admin/college", payload);
      toast.success("Player saved! The Bears in College page is updated.");
      setEditing(null);
      reload();
    } catch {
      toast.error("Could not save the player. Please try again.");
    }
    setSaving(false);
  };

  const remove = async () => {
    try {
      await api.delete(`/admin/college/${deleting.id}`);
      toast.success("Player removed from Bears in College.");
      setDeleting(null);
      reload();
    } catch {
      toast.error("Could not delete the player.");
    }
  };

  if (loading) return <Loading />;

  return (
    <div data-testid="admin-college-section">
      <SectionHeader
        title="Bears in College"
        subtitle="Showcase former Bears playing college baseball. Add a player and they appear on the public page right away."
        action={
          <PrimaryBtn onClick={() => setEditing({ ...EMPTY })} data-testid="add-college-player-button">
            <Plus size={17} /> Add New Player
          </PrimaryBtn>
        }
      />

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4" data-testid="college-list">
        {sorted.length === 0 && (
          <p className="col-span-full bg-white border border-slate-200 rounded-lg p-8 text-center text-slate-500">
            No Bears in College players yet. Click "Add New Player" to add the first one.
          </p>
        )}
        {sorted.map((p) => (
          <div key={p.id} className="bg-white rounded-lg border border-slate-200 p-4 flex items-center gap-4" data-testid={`college-row-${p.id}`}>
            {p.photo ? (
              <img src={p.photo} alt={p.name} className="h-16 w-16 rounded-full object-cover ring-2 ring-[#0F4C2A]/15" />
            ) : (
              <div className="h-16 w-16 rounded-full bg-[#0F4C2A] flex items-center justify-center shrink-0">
                <span className="font-condensed text-lg font-bold text-[#F59E0B]">{p.name.split(" ").map((n) => n[0]).join("")}</span>
              </div>
            )}
            <div className="flex-1 min-w-0">
              <p className="font-condensed text-lg font-bold uppercase text-slate-900 truncate">{p.name}</p>
              <p className="text-xs text-[#D97706] font-bold uppercase tracking-wide truncate">{p.college}</p>
              <p className="text-xs text-slate-500 font-semibold">{[p.position, p.grad_year ? `NHS Class of ${p.grad_year}` : ""].filter(Boolean).join(" · ")}</p>
            </div>
            <button onClick={() => setEditing({ ...p })} className="p-2.5 text-[#0F4C2A] hover:bg-[#0F4C2A]/10 rounded-md" aria-label="Edit player" data-testid={`college-edit-${p.id}`}>
              <Pencil size={16} />
            </button>
            <button onClick={() => setDeleting(p)} className="p-2.5 text-red-700 hover:bg-red-50 rounded-md" aria-label="Delete player" data-testid={`college-delete-${p.id}`}>
              <Trash2 size={16} />
            </button>
          </div>
        ))}
      </div>

      {editing && (
        <Modal title={editing.id ? "Edit College Player" : "Add College Player"} onClose={() => setEditing(null)} testid="college-modal">
          <div className="space-y-4">
            <Field label="Player Name">
              <input className={inputCls} value={editing.name} onChange={(e) => setEditing({ ...editing, name: e.target.value })} placeholder="First and last name" data-testid="college-form-name" />
            </Field>
            <Field label="College / University">
              <input className={inputCls} value={editing.college} onChange={(e) => setEditing({ ...editing, college: e.target.value })} placeholder="e.g. University of Connecticut" data-testid="college-form-college" />
            </Field>
            <Field label="College Baseball Team" hint="e.g. UConn Huskies — optional">
              <input className={inputCls} value={editing.team} onChange={(e) => setEditing({ ...editing, team: e.target.value })} data-testid="college-form-team" />
            </Field>
            <div className="grid grid-cols-2 gap-4">
              <Field label="Position" hint="e.g. P, SS, OF">
                <input className={inputCls} value={editing.position} onChange={(e) => setEditing({ ...editing, position: e.target.value })} data-testid="college-form-position" />
              </Field>
              <Field label="NHS Graduation Year">
                <select className={inputCls} value={editing.grad_year} onChange={(e) => setEditing({ ...editing, grad_year: e.target.value })} data-testid="college-form-grad-year">
                  <option value="">Choose...</option>
                  {YEARS.map((y) => <option key={y} value={y}>{y}</option>)}
                </select>
              </Field>
            </div>
            <Field label="Short Bio (optional)">
              <textarea rows={3} className={inputCls} value={editing.bio} onChange={(e) => setEditing({ ...editing, bio: e.target.value })} data-testid="college-form-bio" />
            </Field>
            <ImagePicker label="Player Photo" value={editing.photo} onChange={(url) => setEditing({ ...editing, photo: url })} testid="college-form-photo" />
            <div className="flex gap-3 pt-2">
              <PrimaryBtn className="flex-1" onClick={save} disabled={saving} data-testid="college-form-save">
                {saving ? "Saving..." : "Save Player"}
              </PrimaryBtn>
              <GhostBtn onClick={() => setEditing(null)} data-testid="college-form-cancel">Cancel</GhostBtn>
            </div>
          </div>
        </Modal>
      )}

      {deleting && (
        <ConfirmDialog
          message={`Remove ${deleting.name} from Bears in College? This cannot be undone.`}
          onConfirm={remove}
          onCancel={() => setDeleting(null)}
          testid="college-delete-confirm"
        />
      )}
    </div>
  );
}
